import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FormioComponent, ConfirmationDialogComponent } from 'dp-formio';
import { ToastrService } from 'ngx-toastr';
import { filter, finalize, take, takeUntil, iif, BehaviorSubject } from 'rxjs';
import { DecisionInput } from '../../models/decision-input.model';
import { ExecuteVisitType } from '../../models/execute-visit.model';
import { Summary } from '../../models/summary.model';
import { BaseComponent } from '../../base.component';
import { CriterionsService, PAGE_SIZE } from '../../services/criterions.service';
import { MasarCriterionsService } from '../../services/masar-criterions.service';
import { MasarAddEditResponseComponent } from './add-edit-response/masar-add-edit-response.dialog';
import { showToast } from '../../../toast.helper';
import { MemberCriterionInformation } from '../../models/criterion-information.mode';
import { PagingResponse } from '../../models/paging-response.model';
import { PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-masar-decision-summary',
  templateUrl: './masar-decision-summary.component.html',
  styleUrls: ['./masar-decision-summary.component.scss', '../data-table.scss']
})
export class MasarDecisionSummaryComponent extends BaseComponent implements OnInit {
  @ViewChild(FormioComponent) formioComponent: FormioComponent;
  @Input() criterionKeys: string[];
  @Input() title: string;
  @Input() type: string;
  @Input() filter: string;
  decisionInput$ = this.criterionsService.decisionInputs$;
  isLoadingResults: boolean = false;
  expandedFilter: boolean = false;
  finalReport = ExecuteVisitType.FinalReport;
  institutionEntries = ExecuteVisitType.InstitutionEntries;
  finalReportWithInstitutionEntries = ExecuteVisitType.FinalReportWithInstitutionEntries;

  displayedColumns = [
    'summary',
    'finalReportResponse',
    'actions'
  ];

  enableEntityResponse = false;
  hasResponse = false;
  enabledSummary = false;
  isLeaderDecision = false;

  get isRecommendations() { return this.type == 'recommendations-summaries' };

  constructor(private matDialog: MatDialog,
    private criterionsService: CriterionsService,
    private masarCriterionsService: MasarCriterionsService,
    private translateService: TranslateService,
    private toastrService: ToastrService) { super(); }

  pageSize = PAGE_SIZE;
  selectedPage = 0;
  totalItems = 0;

  visitMetaData = this.criterionsService.decisionInputs$.getValue().visitMetaData;
  isMasar = this.criterionsService.decisionInputs$.getValue().isMasar;
  dataSource$ = new BehaviorSubject<PagingResponse<MemberCriterionInformation | Summary>>(null);
  dir: "rtl" | "ltr" = this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr';

  ngOnInit() {
    this.mainCriterionListener();

    this.decisionInput$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(input => {

      this.hasResponse = this.isRecommendations && !input.disableEntityResponse &&
        (input.executeVisitType == this.institutionEntries ||
          input.executeVisitType == this.finalReport ||
          input.executeVisitType == this.finalReportWithInstitutionEntries);

      this.enableEntityResponse = !input.disableEntityResponse &&
        (input.executeVisitType == this.institutionEntries ||
          input.executeVisitType == this.finalReport ||
          input.executeVisitType == this.finalReportWithInstitutionEntries);

      this.enabledSummary = !input.viewOnly &&
        (
          input.executeVisitType == this.finalReport ||
          (this.isRecommendations && input.executeVisitType == this.finalReportWithInstitutionEntries)
        );

      this.displayedColumns = [
        this.enableEntityResponse ? 'summaryText' : this.filter,
        'criterionKey',
        'finalReportResponse',
        'leaderDecision',
        'actions',
      ];

      this.getData();
    });
  }

  changePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.selectedPage = event.pageIndex;
    this.getData();
  }

  showResponse(decisionInput: DecisionInput, summary: Summary) {
    const dir = this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr';
    this.matDialog.open(MasarAddEditResponseComponent, {
      width: 'min(80%,900px)',
      direction: dir,
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: false,
      data: {
        summary: summary,
        summaryId: summary.id,
        id: summary.finalReportResponseId,
        visitMetaData: decisionInput.visitMetaData,
        viewOnly: decisionInput.viewOnly || decisionInput.executeVisitType != this.institutionEntries,
        dir: dir
      }
    })
      .afterClosed()
      .pipe(
        filter(data => !!data),
        take(1)
      )
      .subscribe((result: any) => {
        if (this.isLeaderDecision) this.getData();
        else {
          summary.finalReportResponseId = result;
          this.updateResultData();
        }
      });
  }

  private mainCriterionListener() {
    this.masarCriterionsService.updatedMainCriterion$
      .pipe(
        filter(data => !!data && !this.enableEntityResponse),
        takeUntil(this.destroy$)
      )
      .subscribe(_ => {
        this.selectedPage = 0;
        this.getData();
      });
  }

  private getData() {
    this.isLoadingResults = true;
    const data = {
      'visitKey.equals': this.visitMetaData.visitKey,
      'criterionKey.in': this.criterionKeys?.join(),
      size: this.pageSize,
      page: this.selectedPage
    };

    iif(
      () => this.enableEntityResponse,
      this.masarCriterionsService.getPaginatedSummaries(this.criterionsService.decisionInputs$.getValue().visitMetaData.visitKey,
        this.type,
        this.isRecommendations),
      this.masarCriterionsService.getCriterionInformationForAll(data)
    ).pipe(
      finalize(() => this.isLoadingResults = false),
      takeUntil(this.destroy$)
    )
      .subscribe((res) => {
        console.log(res);
        this.dataSource$.next(res);
        this.updateResultData();

        const executeVisitType = this.decisionInput$.value.executeVisitType;
        const disableEntityResponse = this.decisionInput$.value.disableEntityResponse;
        this.isLeaderDecision = this.isRecommendations && !disableEntityResponse &&
          (
            executeVisitType == this.finalReport ||
            executeVisitType == this.finalReportWithInstitutionEntries ||
            (executeVisitType == this.institutionEntries && res.data.some(q => !!q.leaderDecision))
          );
      });
  }

  private updateResultData() {
    if (this.enableEntityResponse && this.isRecommendations) {
      const data = this.criterionsService.results$.getValue();
      data.summaryOfRecommendations = this.dataSource$.value.data as Summary[];
      this.criterionsService.results$.next(data);
    }
  }
}
